import { ProductTile } from '../../components/ProductTile/index.js';

// Helper function to map variants to expected format
function mapVariants(variants, product) {
  const currency_iso = window.theme.currency_iso ? window.theme.currency_iso : 'USD';

  return variants.map((variant) => ({
    ...variant,
    Sellable: variant.inventory_quantity > 0 || variant.available,
    imgs: variant.featured_image?.src || product.featured_image,
    image_url: variant.featured_image?.src || product.featured_image,
    Color: variant.option1 || '',
    option1: variant.option1 || '',
    option2: variant.option2 || '',
    id: variant.id,
    'Product-sku': variant.sku || '',
    Barcode: variant.barcode || variant.sku || '',
    price: `${currency_iso}:${(variant.price / 100).toFixed(2)}`,
    compare_at_price: variant.compare_at_price / 100,
  }));
}

// Helper function to get available color options
function getAvailableOptions(product) {
  if (!product || !product.variants) {
    return [];
  }

  const colorOptions = new Map();

  const createColorOption = (variant) => ({
    Color: variant.option1,
    Barcode: variant.barcode || variant.sku || '',
    variantId: variant.id,
    img: variant.featured_image?.src || product.featured_image,
  });

  const processVariants = (filterCondition) => {
    product.variants.forEach((variant) => {
      if (!filterCondition(variant)) return;

      const color = variant.option1;
      if (!colorOptions.has(color)) {
        colorOptions.set(color, createColorOption(variant));
      }
    });
  };

  // Process variants based on availability
  if (product.variants.some((variant) => variant.available)) {
    processVariants((variant) => variant.available);
  } else {
    processVariants((variant) => variant.id);
  }

  return Array.from(colorOptions.values());
}

// Main export function
export default () => {
  const elements = document.querySelectorAll('[data-product-tiles]');

  elements.forEach((el) => {
    try {
      const jsonElement = el.previousElementSibling;
      if (!jsonElement || !jsonElement.textContent) {
        console.error('No product data found for element:', el);
        return;
      }

      const productData = JSON.parse(jsonElement.textContent);
      if (!productData || !Array.isArray(productData)) {
        console.error('Invalid product data format for element:', el);
        return;
      }

      const flattenedData = productData.flat().filter(Boolean);
      const limit = el.getAttribute('data-product-limit');

      // Clear the container
      const container = el.parentNode;
      container.innerHTML = '';

      // Initialize counter for available products
      let availableProductsCount = 0;
      const productLimit = limit ? parseInt(limit) : Infinity;

      flattenedData.forEach((product, productIndex) => {
        // Basic validation
        if (!product || !product.variants || product.variants.length === 0) {
          return;
        }

        // Try to find available variant, fallback to any variant with valid data
        let firstAvailableVariant = product.variants.find((v) => v.available);

        if (!firstAvailableVariant) {
          // Fallback: find any variant with price and required data
          firstAvailableVariant = product.variants.find(
            (v) => v.price && v.price > 0 && v.id && (v.featured_image?.src || product.featured_image),
          );
        }

        if (!firstAvailableVariant) {
          // Last resort: use first variant if it has basic required data
          const firstVariant = product.variants[0];
          if (firstVariant && firstVariant.id && (firstVariant.featured_image?.src || product.featured_image)) {
            firstAvailableVariant = firstVariant;
          }
        }

        if (!firstAvailableVariant) {
          return;
        }

        if (availableProductsCount >= productLimit) return;
        availableProductsCount++;

        // Create ProductTile container
        const productTileElement = document.createElement('div');
        productTileElement.classList.add('ProductTile', 'ProductTile--vanilla');
        productTileElement.setAttribute('data-product-id', product.id);

        // Map variants to expected format
        const mappedVariants = mapVariants(product.variants, product);
        const availableOptions = getAvailableOptions(product);

        // Calculate pricing with fallbacks
        const basePrice = firstAvailableVariant.price || 0;
        const comparePrice = firstAvailableVariant.compare_at_price || 0;
        const maxComparePrice = product.compare_at_price_max || product.compare_at_price_min || comparePrice;

        // Prepare data for ProductTile constructor
        const productTileData = {
          pid: product.id.toString(),
          title: product.title,
          productUrl: `/products/${product.handle}`,
          productType: product.type || '',
          imageUrl: firstAvailableVariant.featured_image?.src || product.featured_image,
          imageAlt: firstAvailableVariant.featured_image?.alt || product.title,
          price: basePrice / 100,
          compareAtPrice: comparePrice / 100,
          maxCompareAtPrice: maxComparePrice / 100,
          tags: product.tags || [],
          variants: mappedVariants,
          options: availableOptions,
          isRec: true,
          shouldReportClickToAdmind: false,
          selectedColor: firstAvailableVariant.option1 || '',
          recToken: null,
          index: productIndex,
          reviews: null,
          isYotpo: false,
          handle: product.handle,
          productTileNode: productTileElement,
        };

        try {
          // Create ProductTile instance
          new ProductTile(productTileData);

          // Append to container
          container.appendChild(productTileElement);
        } catch (error) {
          console.error('Error creating ProductTile for:', product.title, error);
        }
      });

      // Mark as initialized
      el.setAttribute('initialized', 'true');
    } catch (error) {
      console.error('Error rendering product tiles:', error);
    }
  });
};
